import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, NavigationEnd, Router} from '@angular/router';
import {BehaviorSubject} from 'rxjs';
import {filter} from 'rxjs/operators';

export type AppLayout = 'shell' | 'bare';

/*
 * Which chrome a page gets (plan docs/plans/seo-loop-simple-mode/PLAN.md §7, option A):
 * 'shell' = Hexagon's navbar and breadcrumb (every page), 'bare' = the router outlet
 * alone — the SEO loop simple mode at /loop brings its own header, so a content
 * manager opening the Monday link never meets the Hexagon shell. A route opts in with
 * `data: {layout: 'bare'}`; the deepest activated route wins. Login is unchanged:
 * <app-auth-login> still shows whenever the session is missing.
 */
@Injectable({providedIn: 'root'})
export class LayoutService {

    readonly layout$ = new BehaviorSubject<AppLayout>('shell');

    constructor(private router: Router) {
        this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => {
            this.layout$.next(layoutOf(this.router.routerState.snapshot.root));
        });
    }

    get bare(): boolean {
        return this.layout$.value === 'bare';
    }
}

function layoutOf(root: ActivatedRouteSnapshot): AppLayout {
    let layout: AppLayout = 'shell';
    let route: ActivatedRouteSnapshot | null = root;
    while (route) {
        const value = route.data && route.data['layout'];
        if (value === 'bare' || value === 'shell') layout = value;
        route = route.firstChild;
    }
    return layout;
}
